import React, { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useEntityDoc, useEntityList, useOrgProjects } from '../../data/entities';
import { StatCard, COLORS, fmtNum } from '../../components/charts';
import { apiGet } from '../../utils/backendApi';
import { StatusBadge } from './AppsModule';
import { servicesOf } from '../../components/three/fleetState';

// Org home for admins and owners. Members still get the builder overview.
// Every number is a live read (usage month doc, hourly cost rollup, monitor
// probes, audit trail). Empty means the org has not produced that record yet.

const WEEK = 7 * 24 * 60 * 60 * 1000;
const USAGE_LABEL = {
  aiTurns: 'conversations',
  pipelineRuns: 'workflow runs',
  gatedCommits: 'actions committed',
  hitlCycles: 'reviews resolved',
  jobRuns: 'jobs',
  workloadRuns: 'workloads',
  devspaceLaunches: 'dev studios',
  streamBlockLaunches: 'streams',
  liteBlockRuns: 'block runs',
  remoteBlockRuns: 'remote runs',
  gpuBlockRuns: 'GPU runs',
  containerBlockRuns: 'containers',
  labResearchRuns: 'lab research',
  labBrowserMinutes: 'lab minutes',
  labImages: 'lab images',
};

const money = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `$${Number(v).toFixed(2)}`);

function chipClass(tone) {
  if (tone === 'danger') return 'mf-chip is-danger';
  if (tone === 'warn') return 'mf-chip is-warn';
  if (tone === 'ok') return 'mf-chip is-ok';
  return 'mf-chip';
}

function ago(ts) {
  if (!ts) return '';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function usageBits(counters) {
  return Object.entries(counters || {})
    .filter(([, n]) => Number(n) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));
}

function usageLine(counters) {
  const bits = usageBits(counters).slice(0, 3);
  if (!bits.length) return '—';
  return bits.map(([k, n]) => `${fmtNum(Number(n))} ${USAGE_LABEL[k] || k}`).join(' · ');
}

function activitySum(counters) {
  return Object.values(counters || {}).reduce((s, n) => s + (Number(n) || 0), 0);
}

function isNoiseInstance(a) {
  return ['terminated', 'consumed'].includes(a.status) || /^pool-/.test(a.slug || '');
}

function hostPlane(app) {
  let host = '';
  try { host = new URL(app.urls?.app || '').hostname; } catch { /* no url */ }
  if (host.endsWith('.web.app') || host.endsWith('.firebaseapp.com')) return 'Firebase';
  if (app.delivery === 'external') return 'Own host';
  return '';
}

function probeTone(app) {
  const downSvcs = app.services?.down?.length || 0;
  if (app.status === 'live' && app.monitor?.state === 'down') return 'danger';
  if ((app.scan?.counts?.critical || 0) > 0) return 'danger';
  if (downSvcs || (app.scan?.counts?.high || 0) > 0 || (app.scan?.counts?.medium || 0) > 0) return 'warn';
  if (app.monitor?.tlsDaysLeft != null && app.monitor.tlsDaysLeft < 14) return 'warn';
  if (app.status === 'suspended' || app.status === 'awaiting_payment') return 'warn';
  if (app.status === 'live' && app.monitor?.state === 'up' && !downSvcs) return 'ok';
  return 'muted';
}

function dailySpark(daily) {
  if (!daily) return null;
  return Object.keys(daily).sort().slice(-14).map((d) => Number(daily[d]) || 0);
}

// Three rows is the story; the rest folds open on the same card.
const HIGHLIGHT = 3;
const TONE_RANK = { danger: 0, warn: 1, info: 2, muted: 3, ok: 4 };

function More({ extra, open, onToggle, label }) {
  if (!open && extra <= 0) return null;
  return (
    <button type="button" className="ad-fold" aria-expanded={!!open} onClick={onToggle}>
      {open ? 'Show less' : label}
    </button>
  );
}

export default function AdminDashboard() {
  const { org } = useOutletContext();
  const base = `/orgs/${org.id}`;
  const period = new Date().toISOString().slice(0, 7);
  const { docs: members } = useEntityList('member', { org: org.id });
  const { docs: apps } = useEntityList('appInstance', { org: org.id });
  const { docs: builderApps } = useEntityList('app', { org: org.id });
  const { docs: reviews } = useEntityList('reviewItem', { org: org.id });
  const { docs: integrations } = useEntityList('integration', { org: org.id });
  const { docs: testRuns } = useEntityList('testRun', { org: org.id });
  const { docs: audit } = useEntityList('auditEvent', { org: org.id });
  const projects = useOrgProjects(org.id);
  const { doc: usage, error: usageError } = useEntityDoc('usage', { org: org.id }, period);
  const { doc: rollup, error: costError } = useEntityDoc('orgCost', { org: org.id }, period);
  const [open, setOpen] = useState({});
  const toggle = (key) => setOpen((s) => ({ ...s, [key]: !s[key] }));
  const sliceOf = (key, rows) => (open[key] ? rows : rows.slice(0, HIGHLIGHT));
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    let live = true;
    apiGet('/solutions').then((data) => { if (live) setCatalog(data?.apps || []); }).catch(() => {});
    return () => { live = false; };
  }, []);

  const hosted = useMemo(
    () => (apps || []).filter((a) => !isNoiseInstance(a)).sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0)),
    [apps],
  );

  const attention = useMemo(() => {
    const items = [];
    const state = org.costAlertState;
    if (state && state.level && state.level !== 'ok' && state.message) {
      items.push({ id: 'cost', tone: state.level === 'over' ? 'danger' : 'warn', kind: 'cost', text: state.message, to: `${base}/settings` });
    }
    for (const a of hosted) {
      const name = a.name || a.slug || 'App';
      const to = `${base}/apps/${a.id}`;
      if (a.status === 'live' && a.monitor?.state === 'down') {
        items.push({
          id: `down-${a.id}`, tone: 'danger', kind: 'health', to,
          text: `${name} is down${a.monitor.consecFails ? ` (${a.monitor.consecFails} failed probes)` : ''}`,
        });
      }
      const downSvcs = a.services?.down || [];
      if (a.status === 'live' && a.monitor?.state !== 'down' && downSvcs.length) {
        items.push({ id: `svc-${a.id}`, tone: 'warn', kind: 'health', to, text: `${name}: ${downSvcs.length} service${downSvcs.length === 1 ? '' : 's'} reporting down` });
      }
      if (a.monitor?.tlsDaysLeft != null && a.monitor.tlsDaysLeft < 14) {
        items.push({
          id: `tls-${a.id}`, tone: a.monitor.tlsDaysLeft < 7 ? 'danger' : 'warn', kind: 'tls', to,
          text: `${name} certificate expires in ${a.monitor.tlsDaysLeft} day${a.monitor.tlsDaysLeft === 1 ? '' : 's'}`,
        });
      }
      const crit = a.scan?.counts?.critical || 0;
      const high = a.scan?.counts?.high || 0;
      const med = a.scan?.counts?.medium || 0;
      if (crit || high || med) {
        const parts = [crit ? `${crit} critical` : '', high ? `${high} high` : '', med ? `${med} medium` : ''].filter(Boolean).join(', ');
        items.push({ id: `scan-${a.id}`, tone: crit ? 'danger' : 'warn', kind: 'security', to, text: `${name}: ${parts} open findings` });
      }
      if (a.status === 'provisioning') {
        items.push({ id: `prov-${a.id}`, tone: 'info', kind: 'status', to, text: `${name} is still provisioning` });
      }
      const rep = a.mail?.reputation?.state;
      if (rep === 'throttled' || rep === 'paused' || a.mail?.status === 'paused' || a.mail?.status === 'suspended') {
        items.push({ id: `mail-${a.id}`, tone: 'warn', kind: 'mail', to, text: `${name} mail is ${rep || a.mail.status}` });
      }
      if (a.status === 'suspended' || a.status === 'awaiting_payment') {
        items.push({ id: `st-${a.id}`, tone: 'warn', kind: 'status', to, text: `${name} is ${a.status.replace('_', ' ')}` });
      }
    }
    const pending = (reviews || []).filter((r) => r.status === 'pending');
    const urgent = pending.filter((r) => r.priority === 'urgent' || r.priority === 'high');
    if (urgent.length) {
      items.push({ id: 'rev-urgent', tone: 'danger', kind: 'review', to: `${base}/human-review`, text: `${urgent.length} urgent review${urgent.length === 1 ? '' : 's'} waiting` });
    } else if (pending.length) {
      items.push({ id: 'rev', tone: 'warn', kind: 'review', to: `${base}/human-review`, text: `${pending.length} review${pending.length === 1 ? '' : 's'} waiting on a person` });
    }
    const failing = (integrations || []).filter((i) => i.lastTest && !i.lastTest.ok);
    if (failing.length) {
      items.push({ id: 'int', tone: 'warn', kind: 'integration', to: `${base}/integrations`, text: `${failing.length} integration${failing.length === 1 ? '' : 's'} failed the last connection test` });
    }
    const weekAgo = Date.now() - WEEK;
    const failedQa = (testRuns || []).filter((r) => ['failed', 'error'].includes(r.status) && (r.createdAt || 0) >= weekAgo);
    if (failedQa.length) {
      items.push({ id: 'qa', tone: 'warn', kind: 'qa', to: `${base}/qa`, text: `${failedQa.length} QA run${failedQa.length === 1 ? '' : 's'} failed in the last 7 days` });
    }
    return items;
  }, [org.costAlertState, hosted, reviews, integrations, testRuns, base]);

  const team = useMemo(() => {
    const byUser = usage?.byUser || {};
    const costByUser = rollup?.byUser || {};
    const rows = (members || []).map((m) => ({
      id: m.id,
      name: m.email || m.id,
      role: m.role || 'member',
      counters: byUser[m.id] || byUser[m.uid] || {},
      usd: Number(costByUser[m.id] ?? costByUser[m.uid] ?? 0),
    }));
    if (byUser.system || costByUser.system) {
      rows.push({ id: 'system', name: 'System', role: 'automated', counters: byUser.system || {}, usd: Number(costByUser.system || 0) });
    }
    rows.sort((a, b) => (b.usd - a.usd) || (activitySum(b.counters) - activitySum(a.counters)) || a.name.localeCompare(b.name));
    return rows;
  }, [members, usage, rollup]);

  const attentionReady = apps != null && reviews != null && integrations != null && testRuns != null;
  const live = hosted.filter((a) => a.status === 'live');
  const healthy = live.filter((a) => probeTone(a) === 'ok');
  const weekAgo = Date.now() - WEEK;
  const weekActivity = (audit || []).filter((e) => (e.createdAt || 0) >= weekAgo);
  const counters = usageBits(usage?.counters);
  const byApp = Object.entries(rollup?.byApp || {}).filter(([, a]) => a.mtdUsd > 0).sort((a, b) => b[1].mtdUsd - a[1].mtdUsd);
  const rankedAttention = [...attention].sort((a, b) => (TONE_RANK[a.tone] ?? 9) - (TONE_RANK[b.tone] ?? 9));
  const shownAttention = sliceOf('attn', rankedAttention);
  const designs = (builderApps || []).filter((a) => !a.archived);
  const hostedIds = new Set(hosted.map((a) => a.id));
  // Apps this admin owns that are not already in this org's health table:
  // the Firebase portfolio and anything still on its own host.
  const portfolio = catalog.filter((a) => a.canManage && a.plane !== 'foundation' && !hostedIds.has(a.id));

  const svcOf = (a) => servicesOf(a.monitor);
  const svcWord = (sv) => (sv ? (sv.stale ? `${sv.total} silent` : `${sv.up}/${sv.total}`) : '—');
  const appUsd = (id) => rollup?.byApp?.[id]?.mtdUsd;
  const shownTeam = sliceOf('team', team);
  const shownCosts = sliceOf('cost', byApp);
  const shownProjects = sliceOf('proj', projects || []);
  const rankedHosted = [...hosted].sort((a, b) =>
    (TONE_RANK[probeTone(a)] ?? 9) - (TONE_RANK[probeTone(b)] ?? 9)
    || (Number(appUsd(b.id)) || 0) - (Number(appUsd(a.id)) || 0)
    || (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));
  const shownHealth = sliceOf('health', rankedHosted);
  const activityRows = (audit || []).slice(0, 12);
  const shownActivity = sliceOf('activity', activityRows);
  const restUsd = (rows, pick) => rows.slice(HIGHLIGHT).reduce((s, row) => s + (Number(pick(row)) || 0), 0);
  const teamRest = team.length - HIGHLIGHT;
  const costRest = byApp.length - HIGHLIGHT;
  const healthRest = rankedHosted.slice(HIGHLIGHT);
  const healthRestBad = healthRest.filter((a) => probeTone(a) !== 'ok').length;
  const who = (e) => e.actorEmail || (members || []).find((m) => m.id === e.actor || m.uid === e.actor)?.email || e.actor || '—';

  return (
    <div className="ad-dash is-dense">
      <header className="ad-head">
        <div>
          <h1 style={{ margin: 0 }}>{org.name}</h1>
          <p className="cs-help" title="Usage is live. Costs roll up hourly from the ledger.">Admin dashboard · {period}</p>
        </div>
        <div className="ad-actions">
          <Link to={`${base}/analytics`} className="cs-btn cs-btn-outline cs-btn-sm">Analytics</Link>
          <Link to={`${base}/settings`} className="cs-btn cs-btn-outline cs-btn-sm">Costs</Link>
          <Link to={`${base}/administration?tab=audit`} className="cs-btn cs-btn-outline cs-btn-sm">Audit trail</Link>
          <Link to={`${base}/builder`} className="cs-btn cs-btn-primary cs-btn-sm">Build an app</Link>
        </div>
      </header>

      {/* Seven tiles in one row: the whole month on one line. */}
      <div className="ad-kpis is-dense">
        <StatCard label="People" value={(members || []).length} display={members == null ? '…' : null} color={COLORS.sky} />
        <StatCard
          label="Month to date"
          value={rollup ? Math.round((rollup.mtdUsd || 0) * 100) : 0}
          display={costError ? '—' : rollup === undefined ? '…' : rollup ? money(rollup.mtdUsd) : '—'}
          spark={dailySpark(rollup?.daily)}
          color={COLORS.amber}
        />
        <StatCard label="Burn / day" value={rollup ? Math.round((rollup.burnPerDayUsd || 0) * 100) : 0} display={rollup === undefined ? '…' : rollup ? money(rollup.burnPerDayUsd) : '—'} color={COLORS.amber} />
        <StatCard label="Projected" value={rollup ? Math.round((rollup.projectedUsd || 0) * 100) : 0} display={rollup === undefined ? '…' : rollup ? money(rollup.projectedUsd) : '—'} color={org.costLimits?.monthlyUsd > 0 && rollup?.projectedUsd > org.costLimits.monthlyUsd ? COLORS.red : COLORS.amber} />
        <StatCard
          label="Needs attention"
          value={attention.length}
          display={attentionReady ? null : '…'}
          color={!attentionReady || !attention.length ? COLORS.green : COLORS.red}
        />
        <StatCard
          label="Apps healthy"
          value={healthy.length}
          display={apps == null ? '…' : live.length ? `${healthy.length}/${live.length}` : '—'}
          color={live.length && healthy.length < live.length ? COLORS.red : COLORS.green}
        />
        <StatCard label="Activity · 7d" value={weekActivity.length} display={audit == null ? '…' : null} color={COLORS.violet} />
      </div>

      <section className="cs-card ad-card" aria-label="Needs attention">
        <h2>
          Needs attention
          {attentionReady && !shownAttention.length && <span className="mf-chip is-ok">All clear</span>}
          <Link to={`${base}/human-review`}>Review queue →</Link>
        </h2>
        {!attentionReady && <p className="cs-help" style={{ margin: 0 }}>Checking apps, reviews, and integrations…</p>}
        {attentionReady && !shownAttention.length && <p className="cs-help" style={{ margin: 0 }}>No down apps, open reviews, cost alerts, or failing checks.</p>}
        {!!shownAttention.length && (
          <div className="ad-attn is-grid">
            {shownAttention.map((item) => (
              <Link key={item.id} to={item.to}>
                <span className={`mf-chip is-${item.tone === 'danger' ? 'danger' : item.tone === 'warn' ? 'warn' : 'info'}`}>{item.kind}</span>
                <span className="ad-ell">{item.text}</span>
              </Link>
            ))}
          </div>
        )}
        <More
          extra={rankedAttention.length - HIGHLIGHT}
          open={open.attn}
          onToggle={() => toggle('attn')}
          label={`${rankedAttention.length - HIGHLIGHT} more signal${rankedAttention.length - HIGHLIGHT === 1 ? '' : 's'}`}
        />
      </section>

      <div className="ad-grid is-three">
        <section className="cs-card ad-card" aria-label="Team usage">
          <h2>
            Team usage
            {!!counters.length && <span className="ad-h2-sub">{counters.slice(0, 4).map(([k, n]) => `${fmtNum(Number(n))} ${USAGE_LABEL[k] || k}`).join(' · ')}</span>}
            <Link to={`${base}/administration`}>People →</Link>
          </h2>
          {usageError && <p className="cs-form-error" role="alert">{usageError}</p>}
          {usage === undefined && !usageError && <p className="cs-help">Loading this month’s meters…</p>}
          {usage === null && <p className="cs-help">No usage recorded for {period} yet.</p>}
          <div className="ad-scroll">
            <table className="mf-table ad-dense">
              <thead>
                <tr><th>Person</th><th>This month</th><th className="is-num">Cost</th></tr>
              </thead>
              <tbody>
                {members == null && <tr><td colSpan={3} className="cs-help">Loading people…</td></tr>}
                {members && !team.length && <tr><td colSpan={3} className="cs-help">No members yet.</td></tr>}
                {shownTeam.map((row) => (
                  <tr key={row.id}>
                    <td><span className="ad-ell ad-ell-wide" title={`${row.name} · ${row.role}`}>{row.name} <span className="cs-help ad-role">{row.role}</span></span></td>
                    <td><span className="cs-help ad-ell ad-ell-wide" title={usageLine(row.counters)}>{usageLine(row.counters)}</span></td>
                    <td className="is-num" title="The member’s share of this month’s rollup">{row.usd ? money(row.usd) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <More
            extra={teamRest}
            open={open.team}
            onToggle={() => toggle('team')}
            label={restUsd(team, (r) => r.usd)
              ? `${teamRest} more · ${money(restUsd(team, (r) => r.usd))}`
              : `${teamRest} more ${teamRest === 1 ? 'person' : 'people'}`}
          />
        </section>

        <section className="cs-card ad-card" aria-label="Costs">
          <h2>
            Costs · {period}
            {rollup?.updatedAt && <span className="ad-h2-sub">updated {ago(rollup.updatedAt)}</span>}
            <Link to={`${base}/settings`}>Full ledger →</Link>
          </h2>
          {costError && <p className="cs-form-error" role="alert">{costError}</p>}
          {rollup === undefined && !costError && <p className="cs-help">Loading the rollup…</p>}
          {rollup === null && <p className="cs-help">No cost rollup for {period} yet. The first one lands within an hour of metered activity.</p>}
          {rollup && (
            <>
              {org.costLimits?.monthlyUsd > 0 && (
                <LimitBar mtdUsd={rollup.mtdUsd || 0} projectedUsd={rollup.projectedUsd || 0} limitUsd={org.costLimits.monthlyUsd} />
              )}
              {!byApp.length && <p className="cs-help">No hosted-app spend recorded this month.</p>}
              <ul className="ad-cost-list is-dense">
                {shownCosts.map(([id, a]) => (
                  <li key={id}>
                    <Link to={`${base}/apps/${id}`} className="ad-ell">{a.name || id}</Link>
                    <span className="ad-bar" aria-hidden="true"><span style={{ width: `${Math.max(2, Math.round((a.mtdUsd / (byApp[0][1].mtdUsd || 1)) * 100))}%` }} /></span>
                    <span className="is-num">{money(a.mtdUsd)}</span>
                  </li>
                ))}
              </ul>
              <More
                extra={costRest}
                open={open.cost}
                onToggle={() => toggle('cost')}
                label={`${costRest} more · ${money(restUsd(byApp, ([, a]) => a.mtdUsd))}`}
              />
            </>
          )}
        </section>

        <section className="cs-card ad-card" aria-label="Projects">
          <h2>
            Projects
            {!!projects?.length && <span className="ad-h2-sub">{projects.length}</span>}
          </h2>
          {projects == null && <p className="cs-help">Loading projects…</p>}
          {projects && !projects.length && <p className="cs-help">No projects yet. A project groups work inside a workspace.</p>}
          {!!projects?.length && (
            <div className="ad-scroll">
              <table className="mf-table ad-dense">
                <thead>
                  <tr><th>Project</th><th>Workspace</th><th>Apps</th></tr>
                </thead>
                <tbody>
                  {shownProjects.map((p) => {
                    const inWs = hosted.filter((a) => a.workspaceId === p.wsId);
                    const worst = inWs.reduce((t, a) => {
                      const tone = probeTone(a);
                      if (tone === 'danger' || t === 'danger') return 'danger';
                      if (tone === 'warn' || t === 'warn') return 'warn';
                      if (tone === 'ok') return 'ok';
                      return t;
                    }, 'muted');
                    return (
                      <tr key={`${p.wsId}/${p.id}`}>
                        <td className="ad-ell"><Link to={`${base}/p/${p.wsId}/${p.id}`}>{p.name}</Link></td>
                        <td className="cs-help ad-ell" style={{ margin: 0 }}>{p.wsName}</td>
                        <td>
                          {inWs.length
                            ? <span className={chipClass(worst)} title={`${inWs.length} hosted · ${worst === 'ok' ? 'healthy' : worst === 'muted' ? 'no probe' : worst}`}>{inWs.length} · {worst === 'ok' ? 'healthy' : worst === 'muted' ? 'no probe' : worst}</span>
                            : <span className="cs-help">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                </table>
            </div>
          )}
          <More
            extra={(projects || []).length - HIGHLIGHT}
            open={open.proj}
            onToggle={() => toggle('proj')}
            label={`${(projects || []).length - HIGHLIGHT} more project${(projects || []).length - HIGHLIGHT === 1 ? '' : 's'}`}
          />
        </section>
      </div>

      <div className="ad-bottom">
      <section className="cs-card ad-card" aria-label="Health">
        <h2>
          Health
          {!!hosted.length && <span className="ad-h2-sub">{live.length} live · {healthy.length} healthy · {hosted.length} hosted</span>}
          <Link to={`${base}/deployments?tab=instances`}>All instances →</Link>
        </h2>
        {apps == null && <p className="cs-help">Loading apps…</p>}
        {apps && !hosted.length && !designs.length && (
          <p className="cs-help" style={{ marginTop: 0 }}>No hosted instances or app designs yet. <Link to={`${base}/builder`}>Start in App Builder</Link>.</p>
        )}
        {!!hosted.length && (
          <div className="ad-scroll">
            <table className="mf-table ad-dense">
              <thead>
                <tr><th>Hosted app</th><th>Status</th><th>Probe</th><th>Services</th><th>MCP</th><th>TLS</th><th>Latency</th><th>Security</th><th className="is-num">MTD</th></tr>
              </thead>
              <tbody>
                {shownHealth.map((a) => {
                  const crit = a.scan?.counts?.critical || 0;
                  const high = a.scan?.counts?.high || 0;
                  const med = a.scan?.counts?.medium || 0;
                  const tone = probeTone(a);
                  const accepted = a.scan?.counts?.accepted || 0;
                  const findings = [crit && `${crit} critical`, high && `${high} high`, med && `${med} medium`].filter(Boolean).join(' · ') || (accepted ? `clean · ${accepted} baseline` : '');
                  const sv = svcOf(a);
                  const downSvcs = sv?.down.length || 0;
                  return (
                    <tr key={a.id}>
                      <td className="ad-ell"><Link to={`${base}/apps/${a.id}`}>{a.name || a.slug}</Link>{a.envRole === 'uat' && <span className="pf-preview-tag">UAT</span>}{hostPlane(a) && <span className="pf-preview-tag">{hostPlane(a)}</span>}</td>
                      <td><StatusBadge status={a.status} /></td>
                      <td><span className={chipClass(tone)}>{a.monitor?.state || (a.status === 'live' ? 'no probe' : '—')}</span></td>
                      <td className={downSvcs ? '' : 'cs-help'} style={{ margin: 0 }} title={downSvcs ? `down: ${sv.down.join(', ')}` : ''}>{downSvcs ? <span className="mf-chip is-warn">{svcWord(sv)} · {downSvcs} down</span> : svcWord(sv)}</td>
                      <td title={a.monitor?.mcpError || (a.monitor?.mcp === 'unsupported' ? 'kit predates the built-in MCP server — reprovision to add it' : 'AI clients (Cursor / Claude / ChatGPT) can connect')}>
                        <span className={chipClass(a.monitor?.mcp === 'up' ? 'ok' : a.monitor?.mcp === 'down' ? 'danger' : a.monitor?.mcp === 'unsupported' ? 'warn' : '')}>{a.monitor?.mcp === 'unsupported' ? 'needs kit update' : a.monitor?.mcp || '—'}</span>
                      </td>
                      <td className="cs-help" style={{ margin: 0 }}>{a.monitor?.tlsDaysLeft != null ? `${a.monitor.tlsDaysLeft}d` : '—'}</td>
                      <td className="cs-help" style={{ margin: 0 }}>{a.monitor?.lastLatencyMs != null ? `${a.monitor.lastLatencyMs} ms` : '—'}</td>
                      <td className="cs-help" style={{ margin: 0 }}>{a.scan?.lastScanAt ? (findings || 'clean') : '—'}</td>
                      <td className="is-num">{appUsd(a.id) > 0 ? money(appUsd(a.id)) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <More
          extra={healthRest.length}
          open={open.health}
          onToggle={() => toggle('health')}
          label={healthRestBad
            ? `${healthRest.length} more · ${healthRestBad} need a look`
            : `${healthRest.length} more · all healthy`}
        />
        {(!!portfolio.length || !!designs.length) && open.side && (
          <div className="ad-sub-grid">
            {!!portfolio.length && (
              <div>
                <h3 title="You admin these. They stay where they are stable, and anyone signed in can open them from Solutions.">On Firebase or their own host</h3>
                <ul className="ad-cost-list is-dense">
                  {portfolio.map((a) => (
                    <li key={a.id}>
                      <span className="ad-ell">
                        <Link to={`/orgs/${a.orgId}/apps/${a.id}`}>{a.name || a.slug}</Link>
                        <span className="pf-preview-tag">{a.plane === 'firebase' ? 'Firebase' : 'Own host'}</span>
                      </span>
                      <span>
                        {a.url && <a href={a.url} target="_blank" rel="noreferrer" className="cs-help">Open ↗</a>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!!designs.length && (
              <div>
                <h3>App designs</h3>
                <ul className="ad-cost-list is-dense">
                  {designs.map((a) => (
                    <li key={a.id}>
                      <Link to={`${base}/builder?app=${a.id}`} className="ad-ell">{a.name || 'Untitled'}</Link>
                      <span className="mf-chip">{a.stage || 'define'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {(!!portfolio.length || !!designs.length) && (
          <More
            extra={portfolio.length + designs.length}
            open={open.side}
            onToggle={() => toggle('side')}
            label={[
              portfolio.length ? `${portfolio.length} on another host` : '',
              designs.length ? `${designs.length} design${designs.length === 1 ? '' : 's'}` : '',
            ].filter(Boolean).join(' · ')}
          />
        )}
      </section>

      <section className="cs-card ad-card" aria-label="Recent activity">
        <h2>
          Recent activity
          <Link to={`${base}/administration?tab=audit`}>Full trail →</Link>
        </h2>
        {audit == null && <p className="cs-help">Loading activity…</p>}
        {audit && !audit.length && <p className="cs-help" style={{ marginTop: 0 }}>No server actions yet. Deploys, publishes, access changes, and membership updates land here.</p>}
        {!!shownActivity.length && (
          <ul className="ad-act">
            {shownActivity.map((e) => (
              <li key={e.id} title={[who(e), e.target].filter(Boolean).join(' · ')}>
                <span className="cs-help" title={e.createdAt ? new Date(e.createdAt).toLocaleString() : ''}>{ago(e.createdAt) || '—'}</span>
                <span className="ad-ell">{(e.action || '—').replace(/\./g, ' · ')}</span>
                <span className="cs-help ad-ell ad-who">{who(e)}</span>
                <span className="cs-help ad-ell ad-target">{e.target || '—'}</span>
              </li>
            ))}
          </ul>
        )}
        <More
          extra={activityRows.length - HIGHLIGHT}
          open={open.activity}
          onToggle={() => toggle('activity')}
          label={`${activityRows.length - HIGHLIGHT} more event${activityRows.length - HIGHLIGHT === 1 ? '' : 's'}`}
        />
      </section>
      </div>
    </div>
  );
}

function LimitBar({ mtdUsd, projectedUsd, limitUsd }) {
  const pct = Math.min(100, (mtdUsd / limitUsd) * 100);
  const ppct = Math.min(100, (projectedUsd / limitUsd) * 100);
  const color = pct >= 100 ? 'var(--danger)' : pct >= 80 || ppct >= 100 ? 'var(--warn)' : 'var(--ok)';
  return (
    <div className="ad-limit">
      <div className="ad-limit-track">
        <div className="ad-limit-proj" style={{ width: `${ppct}%`, background: color }} />
        <div className="ad-limit-mtd" style={{ width: `${pct}%`, background: color }} />
      </div>
      <p className="cs-help" style={{ margin: '4px 0 0' }} title="Alerts only — nothing is paused.">
        {Math.round((mtdUsd / limitUsd) * 100)}% of {money(limitUsd)} · projected {Math.round((projectedUsd / limitUsd) * 100)}%
      </p>
    </div>
  );
}
